/**
 * Cognito Custom Auth Flow Client
 *
 * Handles the CUSTOM_AUTH flow with SRP (CUSTOM_WITH_SRP) and challenge triggers:
 * 1. SRP password verification (handled by Cognito + amazon-cognito-identity-js)
 * 2. Custom challenge: WebAuthn MFA, or an emailed one-time code when the user
 *    has no passkey yet (they must then enroll one), or device token skip.
 *    The app client allows ONLY this flow — there is no password-only login.
 *
 * Uses amazon-cognito-identity-js for SRP math and challenge handling.
 *
 * Security notes:
 * - SRP ensures password never leaves the client (zero-knowledge proof)
 * - Custom challenges are verified server-side by Lambda triggers
 * - Device token is sent as clientMetadata in the SRP response
 * - Tokens are stored securely via the existing store module
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { COGNITO } from "../config";
import { getDeviceToken, saveDeviceToken } from "./device-token";
import { saveTokens, clearTokens, type TokenSet } from "./store";
import type { AssertionOptions, AuthenticationResult } from "./webauthn";

// ─── Types ────────────────────────────────────────────────────────────────────

export type AuthFlowState =
  | { step: "IDLE" }
  | { step: "AUTHENTICATING" }
  | { step: "PASSKEY_CHALLENGE"; assertionOptions: AssertionOptions }
  | { step: "EMAIL_CODE"; emailHint: string; error?: string }
  | { step: "ENROLL_REQUIRED" }
  | { step: "SUCCESS"; tokens: TokenSet }
  | { step: "ERROR"; message: string };

export type OnStateChange = (state: AuthFlowState) => void;

// ─── Cognito Setup ────────────────────────────────────────────────────────────

function getUserPool(): CognitoUserPool {
  return new CognitoUserPool({
    UserPoolId: COGNITO.userPoolId,
    ClientId: COGNITO.clientId,
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function sessionToTokens(session: CognitoUserSession): TokenSet {
  return {
    access_token: session.getAccessToken().getJwtToken(),
    id_token: session.getIdToken().getJwtToken(),
    refresh_token: session.getRefreshToken()?.getToken(),
    expires_at: session.getAccessToken().getExpiration(),
  };
}

/**
 * After successful MFA, request a device token from the server.
 * The token allows skipping MFA on this device for 30 days.
 */
async function requestDeviceToken(accessToken: string): Promise<void> {
  try {
    const resp = await fetch(`${COGNITO.apiBase}/auth/remember-device`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (resp.ok) {
      const data = await resp.json();
      if (data.device_token) {
        saveDeviceToken(data.device_token);
      }
    }
  } catch (err) {
    // Non-fatal: device just won't be remembered
    console.warn("[cognito-auth] Failed to get device token:", err);
  }
}

// ─── IP-Bound Device Validation ──────────────────────────────────────────────

/**
 * Validate the device token against the current IP address.
 * Returns a short-lived session_token if IP matches, or empty string if not.
 *
 * This is called BEFORE Cognito auth. The session_token is passed in
 * clientMetadata so DefineAuthChallenge can trust the device+IP validation
 * was done server-side.
 */
async function validateDeviceToken(deviceToken: string): Promise<string> {
  try {
    const resp = await fetch(`${COGNITO.apiBase}/auth/validate-device`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_token: deviceToken }),
    });
    if (resp.ok) {
      const data = await resp.json();
      if (data.valid && data.session_token) {
        return data.session_token;
      }
    }
    return "";
  } catch (err) {
    console.warn("[cognito-auth] Device validation failed:", err);
    return "";
  }
}

// ─── Custom Auth Flow ─────────────────────────────────────────────────────────

/**
 * Initiate the auth flow with email + password.
 *
 * Flow:
 * 1. If device token exists, validate it against current IP (/auth/validate-device)
 * 2. Perform SRP key exchange (password verification without sending password)
 * 3. DefineAuthChallenge checks session_token (valid = skip MFA)
 * 4. If no valid session_token: CUSTOM_CHALLENGE for WebAuthn MFA
 *
 * The onStateChange callback is called at each step so the UI can react.
 */
export async function initiateCustomAuth(
  email: string,
  password: string,
  options: {
    rememberDevice: boolean;
    onStateChange: OnStateChange;
    onPasskeyChallenge: (opts: AssertionOptions) => Promise<AuthenticationResult>;
    /** Resolves with the 6-digit code the user typed (EMAIL_OTP challenge). */
    onEmailCode: (emailHint: string) => Promise<string>;
    /** Called after an email-code sign-in: the user has no passkey and must enroll one. */
    onMustEnroll: () => void;
  }
): Promise<TokenSet> {
  const { rememberDevice, onStateChange, onPasskeyChallenge, onEmailCode, onMustEnroll } = options;

  onStateChange({ step: "AUTHENTICATING" });

  // Step 1: If we have a device token, validate it (checks IP match server-side)
  const deviceToken = getDeviceToken();
  let sessionToken = "";
  if (deviceToken) {
    sessionToken = await validateDeviceToken(deviceToken);
  }

  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    const user = new CognitoUser({
      Username: email,
      Pool: pool,
    });
    // CUSTOM_AUTH + password = CUSTOM_WITH_SRP: the library starts with
    // CHALLENGE_NAME=SRP_A so the Define/Create/Verify triggers run.
    user.setAuthenticationFlowType("CUSTOM_AUTH");

    // ClientMetadata is passed with the RespondToAuthChallenge(PASSWORD_VERIFIER) call
    // and is available to the DefineAuthChallenge Lambda trigger
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
      ClientMetadata: {
        session_token: sessionToken,
        remember_device: rememberDevice ? "true" : "false",
      },
    } as any); // 'as any' because @types may not include ClientMetadata

    user.authenticateUser(authDetails, {
      onSuccess: async (session: CognitoUserSession) => {
        // Auth completed without MFA (device was remembered)
        const tokens = sessionToTokens(session);
        saveTokens(tokens);
        onStateChange({ step: "SUCCESS", tokens });
        resolve(tokens);
      },

      onFailure: (err: any) => {
        // Log only the error code to avoid leaking user-existence info
        // (e.g., "User does not exist" vs "Incorrect password")
        console.error("[cognito-auth] Auth failed:", err.code || "UNKNOWN");
        // Return a generic message to the UI regardless of the actual error
        const message = "Authentication failed";
        onStateChange({ step: "ERROR", message });
        reject(new Error(message));
      },

      customChallenge: (challengeParameters: any) => {
        const action = challengeParameters.action;

        if (action === "EMAIL_OTP") {
          const emailHint = challengeParameters.email_hint as string;
          onStateChange({ step: "EMAIL_CODE", emailHint });
          onEmailCode(emailHint)
            .then((code) => {
              user.sendCustomChallengeAnswer(code, {
                onSuccess: (session: CognitoUserSession) => {
                  const tokens = sessionToTokens(session);
                  saveTokens(tokens);
                  onMustEnroll();
                  resolve(tokens);
                },
                onFailure: (err: any) => {
                  // A wrong code ends the Cognito session: the user signs in again.
                  console.error("[cognito-auth] Email code rejected:", err.code || "UNKNOWN");
                  const message = "Wrong or expired code. Please sign in again.";
                  onStateChange({ step: "ERROR", message });
                  reject(new Error(message));
                },
                customChallenge: () => {
                  onStateChange({ step: "ERROR", message: "Unexpected auth state" });
                  reject(new Error("Unexpected second challenge"));
                },
              });
            })
            .catch((err) => {
              onStateChange({ step: "IDLE" });
              reject(err);
            });
          return;
        }

        if (action === "ENROLL") {
          // User has no passkeys — needs enrollment
          onStateChange({ step: "ENROLL_REQUIRED" });
          reject(new Error("ENROLL_REQUIRED"));
          return;
        }

        if (action === "AUTHENTICATE") {
          // WebAuthn MFA challenge
          const assertionOptions: AssertionOptions = JSON.parse(
            challengeParameters.assertionOptions
          );
          onStateChange({ step: "PASSKEY_CHALLENGE", assertionOptions });

          // Wait for the user to complete WebAuthn authentication
          onPasskeyChallenge(assertionOptions)
            .then((assertion) => {
              // Send the assertion back to Cognito
              user.sendCustomChallengeAnswer(
                JSON.stringify(assertion),
                {
                  onSuccess: async (session: CognitoUserSession) => {
                    const tokens = sessionToTokens(session);
                    saveTokens(tokens);

                    // If "remember device" was checked, get a device token
                    if (rememberDevice) {
                      await requestDeviceToken(tokens.access_token);
                    }

                    onStateChange({ step: "SUCCESS", tokens });
                    resolve(tokens);
                  },
                  onFailure: (err: any) => {
                    const message = err.message || "MFA verification failed";
                    onStateChange({ step: "ERROR", message });
                    reject(new Error(message));
                  },
                  customChallenge: () => {
                    onStateChange({ step: "ERROR", message: "Unexpected auth state" });
                    reject(new Error("Unexpected second challenge"));
                  },
                },
                {
                  remember_device: rememberDevice ? "true" : "false",
                }
              );
            })
            .catch((err) => {
              onStateChange({
                step: "ERROR",
                message: err.message || "Passkey authentication failed",
              });
              reject(err);
            });
        }
      },
    });
  });
}

// ─── Forgot Password ─────────────────────────────────────────────────────────

/**
 * Trigger Cognito to send a password reset verification code to the user's email.
 */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err: any) => reject(err),
      inputVerificationCode: () => resolve(),
    });
  });
}

/**
 * Complete the password reset by submitting the verification code and new password.
 */
export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err: any) => reject(err),
    });
  });
}

/**
 * Logout: clear session tokens.
 * Device token is preserved (it's device-specific, survives logout).
 */
export function logoutCustomAuth(): void {
  clearTokens();
}

/**
 * Forget the current device (require MFA on next login).
 */
export function forgetDevice(): void {
  import("./device-token").then((m) => m.clearDeviceToken());
}
